import type {
  CriticalControl,
  ControlledDeliverable,
  DesignRisk,
  DocumentRevision,
  EvidenceItem,
  FieldVerificationRecord,
  HirarcAssessment,
  IncidentRecord,
  Project,
  ProjectWorkspace,
  RegulatoryChangeRecord,
  RevisionComparison,
  SafetyIssuePin,
} from "../types/domain";

const STORAGE_KEY = "dsm-workspace-v4";

const isoNow = () => new Date().toISOString();
const id = () => crypto.randomUUID();

export const blankProject = (): Project => ({
  id: id(),
  name: "",
  location: "",
  regulatoryRegion: "unresolved",
  sector: "",
  workType: "renovation",
  description: "",
  clientName: "",
  startDate: "",
  endDate: "",
  workingDays: null,
  personDays: null,
  contractorCount: null,
  hasDesignWork: true,
  domesticClient: false,
  projectNumber: "",
  currentPhase: "pre-construction",
  procurementRoute: "",
  structureType: "",
  projectValue: null,
  languages: ["English"],
  peakWorkers: null,
  usesHazardousChemicals: false,
  hazardousNoisePotential: false,
  regulatedPlant: false,
  specialSchemeInspection: false,
  majorHazardPotential: false,
  reportableEvent: false,
  regulatoryExposureConfirmed: false,
  createdAt: isoNow(),
  updatedAt: isoNow(),
});

export const blankRisk = (): DesignRisk => ({
  id: id(),
  designElement: "",
  lifecycleStage: "construction",
  hazard: "",
  affectedPeople: "",
  eliminationConsidered: "",
  designDecision: "",
  residualRisk: "not-assessed",
  control: "",
  drawingReference: "",
  owner: "",
  evidenceIds: [],
  reviewStatus: "draft",
  reviewer: "",
  reviewDate: "",
  category: "",
  origin: "design",
  initialLikelihood: null,
  initialSeverity: null,
  residualLikelihood: null,
  residualSeverity: null,
  regulationIds: [],
  controlIds: [],
  revisionIds: [],
  incidentIds: [],
  dueDate: "",
  tags: [],
  reopenedAt: "",
  reopenReason: "",
  previousReviewStatus: "",
});

export const blankEvidence = (): EvidenceItem => ({
  id: id(),
  title: "",
  type: "other",
  reference: "",
  owner: "",
  status: "requested",
  version: "",
  reviewDate: "",
  notes: "",
  fileName: "",
  filePath: "",
  fileSize: null,
  mimeType: "",
  checksum: "",
  uploadedAt: "",
  linkedDutyIds: [],
});

export const blankHirarc = (): HirarcAssessment => ({
  id: id(),
  activity: "",
  location: "",
  hazard: "",
  consequence: "",
  personsAtRisk: "",
  existingControls: "",
  likelihood: 1,
  severity: 1,
  riskScore: 1,
  recommendedControls: "",
  controlHierarchy: "elimination",
  owner: "",
  dueDate: "",
  status: "open",
  residualLikelihood: 1,
  residualSeverity: 1,
  residualRiskScore: 1,
  reviewDate: "",
  reviewTrigger: "Change, incident or scheduled review",
  evidenceIds: [],
  linkedRiskIds: [],
  createdAt: isoNow(),
  updatedAt: isoNow(),
});

export const blankIncident = (): IncidentRecord => ({
  id: id(),
  title: "",
  occurredAt: "",
  location: "",
  eventType: "near-miss",
  severity: "minor",
  description: "",
  immediateCauses: "",
  rootCauses: "",
  failedControls: "",
  lessons: "",
  sourceUrl: "",
  jurisdiction: "Malaysia",
  status: "open",
  owner: "",
  linkedRiskIds: [],
  linkedControlIds: [],
  createdAt: isoNow(),
  updatedAt: isoNow(),
});

export const blankCriticalControl = (): CriticalControl => ({
  id: id(),
  title: "",
  majorAccidentScenario: "",
  controlType: "preventive",
  hierarchy: "engineering",
  performanceStandard: "",
  verificationMethod: "",
  owner: "",
  verificationFrequency: "Before work and weekly",
  health: "not-verified",
  lastVerifiedDate: "",
  nextDueDate: "",
  evidenceIds: [],
  linkedRiskIds: [],
  notes: "",
  createdAt: isoNow(),
  updatedAt: isoNow(),
});

export const blankDocumentRevision = (): DocumentRevision => ({
  id: id(),
  documentNumber: "",
  title: "",
  documentType: "drawing",
  discipline: "",
  revision: "P01",
  status: "draft",
  issueDate: "",
  owner: "",
  fileName: "",
  filePath: "",
  mimeType: "",
  fileSize: null,
  checksum: "",
  supersedesId: "",
  changeSummary: "",
  linkedRiskIds: [],
  linkedDutyIds: [],
  reviewStatus: "draft",
  extractedText: "",
  extractedPages: [],
  textExtractionStatus: "not-run",
  ifcSchema: "",
  ifcEntityCount: null,
  ifcSafetyEntities: [],
  comparisonId: "",
  createdAt: isoNow(),
  updatedAt: isoNow(),
});

export const blankRevisionComparison = (): RevisionComparison => ({
  id: id(),
  previousRevisionId: "",
  currentRevisionId: "",
  comparedAt: isoNow(),
  engineVersion: "DSM-DIFF-1.0",
  addedText: [],
  removedText: [],
  changedPages: [],
  safetyKeywords: [],
  changeScore: 0,
  impactLevel: "none",
  impactedRiskIds: [],
  reopenedRiskIds: [],
  reviewStatus: "machine-screened",
  reviewer: "",
  reviewNotes: "",
});

export const blankDeliverable = (
  type: ControlledDeliverable["type"] = "pci",
): ControlledDeliverable => ({
  id: id(),
  type,
  title: "",
  reference: "",
  revision: "P01",
  status: "not-started",
  owner: "",
  reviewer: "",
  generatedAt: "",
  issuedAt: "",
  policyPackVersion: "",
  sections: [],
  limitationNotes: "",
  approvalId: "",
});

export const blankRegulatoryChange = (): RegulatoryChangeRecord => ({
  id: id(),
  instrument: "",
  previousPackVersion: "",
  newPackVersion: "",
  effectiveDate: "",
  publishedDate: "",
  summary: "",
  sourceUrl: "",
  affectedRuleIds: [],
  impactStatus: "unassessed",
  impactedDutyIds: [],
  impactedRiskIds: [],
  owner: "",
  dueDate: "",
  decision: "",
  assessedAt: "",
});

export const blankFieldVerification = (): FieldVerificationRecord => ({
  id: id(),
  token: crypto.randomUUID(),
  entityType: "control",
  entityId: "",
  title: "",
  verificationType: "point-of-work",
  assignedTo: "",
  createdAt: isoNow(),
  expiresAt: "",
  status: "open",
  verifiedAt: "",
  verifiedBy: "",
  observation: "",
  location: "",
  evidenceReference: "",
});

export const blankWorkspace = (): ProjectWorkspace => ({
  schemaVersion: 3,
  project: blankProject(),
  risks: [],
  evidence: [],
  dutyActions: [],
  hirarcAssessments: [],
  incidents: [],
  criticalControls: [],
  approvals: [],
  documentRevisions: [],
  revisionComparisons: [],
  safetyIssuePins: [],
  deliverables: [],
  regulatoryChanges: [],
  fieldVerifications: [],
  competenceRecords: [],
  trainingAssignments: [],
  contractors: [],
  auditTrail: [],
});

export const upgradeWorkspace = (workspace: Partial<ProjectWorkspace>): ProjectWorkspace => {
  const base = blankWorkspace();
  const project = { ...base.project, ...(workspace.project ?? {}) };
  const risks = Array.isArray(workspace.risks)
    ? workspace.risks.map((risk) => ({ ...blankRisk(), ...risk }))
    : [];
  const evidence = Array.isArray(workspace.evidence)
    ? workspace.evidence.map((item) => ({ ...blankEvidence(), ...item }))
    : [];

  return {
    ...base,
    ...workspace,
    schemaVersion: 3,
    project,
    risks,
    evidence,
    dutyActions: workspace.dutyActions ?? [],
    hirarcAssessments: workspace.hirarcAssessments ?? [],
    incidents: workspace.incidents ?? [],
    criticalControls: workspace.criticalControls ?? [],
    approvals: workspace.approvals ?? [],
    documentRevisions: (workspace.documentRevisions ?? []).map((item) => ({
      ...blankDocumentRevision(),
      ...item,
    })),
    revisionComparisons: workspace.revisionComparisons ?? [],
    safetyIssuePins: (workspace.safetyIssuePins ?? []).map((item) => {
      const upgraded = item as SafetyIssuePin;
      return {
        ...upgraded,
        bcfGuid: upgraded.bcfGuid ?? "",
        priority: upgraded.priority ?? "normal",
        viewpointReference: upgraded.viewpointReference ?? "",
      };
    }),
    deliverables: workspace.deliverables ?? [],
    regulatoryChanges: workspace.regulatoryChanges ?? [],
    fieldVerifications: workspace.fieldVerifications ?? [],
    competenceRecords: workspace.competenceRecords ?? [],
    trainingAssignments: workspace.trainingAssignments ?? [],
    auditTrail: workspace.auditTrail ?? [],
  };
};

export const demoWorkspace = (): ProjectWorkspace => {
  const project: Project = {
    ...blankProject(),
    name: "KL Sentral Tower Extension",
    location: "Kuala Lumpur",
    regulatoryRegion: "peninsular-malaysia",
    sector: "Commercial",
    workType: "new-construction",
    description: "72-storey commercial tower adjacent to live MRT lines, requiring deep excavation and complex logistics.",
    clientName: "Sentral Premier Development",
    startDate: "2026-10-01",
    endDate: "2030-05-15",
    workingDays: 950,
    personDays: 450000,
    contractorCount: 12,
    hasDesignWork: true,
    projectNumber: "DSM-DEMO-KLS",
    currentPhase: "construction",
    procurementRoute: "Design and build",
    structureType: "High-rise",
    projectValue: 850_000_000,
    languages: ["English", "Bahasa Malaysia"],
    peakWorkers: 800,
    usesHazardousChemicals: true,
    hazardousNoisePotential: true,
    regulatedPlant: true,
    specialSchemeInspection: true,
    majorHazardPotential: true,
    reportableEvent: false,
    regulatoryExposureConfirmed: true,
  };

  const contractors = [
    {
      id: "c-1",
      name: "Bina Raya Construction",
      tier: "principal" as const,
      parentId: null,
      status: "verified" as const,
      readinessScore: 98,
      joinedAt: isoNow(),
      evidence: [
        { id: id(), type: "cidb" as const, documentReference: "CIDB-BRC-26", expiryDate: "2028-01-01T00:00:00Z", verified: true }
      ]
    },
    {
      id: "c-2",
      name: "Apex Formwork & Scaffolding",
      tier: "specialist" as const,
      parentId: "c-1",
      status: "non-compliant" as const,
      readinessScore: 45,
      joinedAt: isoNow(),
      evidence: [
        { id: id(), type: "specialist-license" as const, documentReference: "SCAFF-EXPIRED", expiryDate: "2026-06-01T00:00:00Z", verified: false }
      ]
    },
    {
      id: "c-3",
      name: "Deep Earth Piling Sdn Bhd",
      tier: "sub-contractor" as const,
      parentId: "c-1",
      status: "verified" as const,
      readinessScore: 92,
      joinedAt: isoNow(),
      evidence: [
        { id: id(), type: "cidb" as const, documentReference: "CIDB-DEP-26", expiryDate: "2027-11-01T00:00:00Z", verified: true }
      ]
    }
  ];

  const risks: DesignRisk[] = [
    {
      ...blankRisk(),
      id: "dr-1",
      designElement: "Deep Excavation (Zone B)",
      lifecycleStage: "construction",
      hazard: "Catastrophic retaining wall failure affecting live MRT tunnel (3m proximity).",
      affectedPeople: "Public transit users, site workers",
      eliminationConsidered: "Cannot eliminate due to site boundary constraints.",
      designDecision: "Implemented Secant Pile Wall with ground anchors and real-time inclinometer monitoring.",
      residualRisk: "high",
      control: "Daily ground movement readings, PTW required for piling.",
      owner: "Lead Geotechnical Engineer",
      initialLikelihood: 4,
      initialSeverity: 5,
      residualLikelihood: 2,
      residualSeverity: 5,
      reviewStatus: "approved",
      reviewer: "Principal Designer",
      reviewDate: "2026-08-01",
    },
    {
      ...blankRisk(),
      id: "dr-2",
      designElement: "Tower Crane Over-swing",
      lifecycleStage: "construction",
      hazard: "Crane load collapse over public thoroughfare (Jalan Tun Sambanthan).",
      affectedPeople: "Public pedestrians, vehicles",
      eliminationConsidered: "Cannot eliminate, required for core construction.",
      designDecision: "Anti-collision system, zoning restrictions, and lifting only during night hours for heavy elements.",
      residualRisk: "medium",
      control: "Zonal limiters active, daily crane inspection.",
      owner: "Lifting Coordinator",
      initialLikelihood: 3,
      initialSeverity: 5,
      residualLikelihood: 1,
      residualSeverity: 5,
      reviewStatus: "approved",
      reviewer: "Principal Designer",
      reviewDate: "2026-08-10",
    },
    {
      ...blankRisk(),
      id: "dr-3",
      designElement: "Cantilevered Scaffolding",
      lifecycleStage: "construction",
      hazard: "Structural collapse of formwork during high-wind concrete pour.",
      affectedPeople: "Concrete gang, public below",
      eliminationConsidered: "Jump-form considered but ruled out due to architectural slab curves.",
      designDecision: "Double-tied scaffolding with specialized structural sign-off.",
      residualRisk: "high",
      control: "Specialist contractor (Apex) must verify daily.",
      owner: "Structural Engineer",
      initialLikelihood: 3,
      initialSeverity: 5,
      residualLikelihood: 2,
      residualSeverity: 5,
      reviewStatus: "reviewed",
    }
  ];

  const hirarcAssessments: HirarcAssessment[] = [
    {
      ...blankHirarc(),
      id: "h-1",
      activity: "Erecting cantilever scaffolding on Level 45",
      location: "Level 45 - North Face",
      hazard: "Fall from height, dropped objects",
      consequence: "Fatality",
      personsAtRisk: "Scaffolders, ground workers",
      existingControls: "Harnesses, debris netting",
      likelihood: 4,
      severity: 5,
      riskScore: 20,
      recommendedControls: "100% tie-off, drop zones cordoned off, daily PTW.",
      controlHierarchy: "engineering",
      owner: "Apex Formwork", // Non-compliant contractor!
      status: "open",
      residualLikelihood: 3,
      residualSeverity: 5,
      residualRiskScore: 15, // High Risk
    }
  ];

  return {
    ...blankWorkspace(),
    project,
    contractors,
    risks,
    hirarcAssessments,
    auditTrail: [
      {
        id: id(),
        occurredAt: isoNow(),
        actor: "System",
        action: "COMPLIANCE_FAILED",
        entityType: "Contractor",
        entityId: "c-2",
        summary: "Apex Formwork & Scaffolding specialist license expired. PTWs blocked."
      }
    ]
  };
};

export const saveWorkspace = (workspace: ProjectWorkspace) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(workspace));
  } catch (e) {
    console.warn("Failed to save to localStorage", e);
  }
};

export const loadWorkspace = (): ProjectWorkspace | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    const parsed = JSON.parse(data);
    return upgradeWorkspace(parsed);
  } catch (e) {
    console.warn("Failed to load from localStorage", e);
    return null;
  }
};

export const clearWorkspace = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn("Failed to clear localStorage", e);
  }
};
